"use client";

import { VehicleFeature } from "@alba-cars/common-modules";
import { CarIcon } from "lucide-react";
import Image from "next/image";
import { FC, useEffect, useState } from "react";

import IconArrowRight from "@/components/Icons/IconArrowRight";
import { formatImageUrl } from "@/lib/utils";

interface CarDetailsOptionsProps {
  options: VehicleFeature[];
}

const CarDetailsOptions: FC<CarDetailsOptionsProps> = ({ options }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  // Only show "Show More" button if we have more than 5 options
  const shouldShowToggle = isMobile && options.length > 5;

  // Display all options if expanded or not mobile, otherwise just show first 5
  const displayedFeatures = isMobile && !isExpanded ? options.slice(0, 5) : options;

  const chunkedOptions = isMobile
    ? Array.from({ length: Math.ceil(displayedFeatures.length / 4) }, (_, index) =>
        displayedFeatures.slice(index * 4, index * 4 + 4),
      )
    : Array.from({ length: Math.ceil(displayedFeatures.length / 3) }, (_, index) =>
        displayedFeatures.slice(index * 3, index * 3 + 3),
      );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-x-4 gap-y-6 rounded-3xl bg-secondary-light p-10 transition-all duration-300 ease-in-out sm:grid-cols-2 lg:grid-cols-3">
        {chunkedOptions.map((chunk, index) => (
          <div key={index}>
            <ul className="space-y-6">
              {chunk.map((option, optionIndex) => (
                <li key={optionIndex} className="flex items-center font-inter text-sm font-medium text-black">
                  {option?.icon ? (
                    <div className="relative h-[30px] w-[30px] rounded-full">
                      <Image
                        fill
                        priority
                        src={formatImageUrl(option.icon) ?? "/assets/images/default-icon.svg"}
                        alt={option.name}
                        className="rounded-3xl object-cover"
                      />
                    </div>
                  ) : (
                    <CarIcon />
                  )}
                  <span className="ml-2">{option.name}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {shouldShowToggle && (
          <div className="text-center">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-x-2 font-inter text-sm font-medium text-primary hover:text-primary/80"
            >
              {isExpanded ? "Show Less" : "Show More"}
              <IconArrowRight
                fill="red"
                className={`rotate-${isExpanded ? "[25deg]" : "90"} transition-transform duration-300 ease-in-out`}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CarDetailsOptions;
